import { Component, h, Host, Prop } from "@stencil/core";
import { track } from "../../utils/tracking";

const baseClass = "ux-iws-nb-technical-data";

/**
 * Component to display technical data with a toggleable content section.
 * @slot title - The title of the content section.
 * @slot content - The content of the technical data section. Should contain a `<ux-iws-nb-data-table>` component or similar.
 * @slot item - An individual item to be displayed in the top of the technical data section.
 * Should be a simple div with a text. The first part of the text may be wrapped inside <strong>.
 */
@Component({
    tag: "ux-iws-nb-technical-data",
    styleUrl: "nb-technical-data.scss",
    shadow: false,
})
export class NbTechnicalData {
    private static uidIncrement = 0;
    private uid = `${baseClass}-${NbTechnicalData.uidIncrement++}`;
    /** Label for the open button */
    @Prop() openLabel?: string;
    /** Label for the close button */
    @Prop() closeLabel?: string;
    /** Whether the content is expanded or collapsed */
    @Prop({ mutable: true, reflect: true }) opened: boolean = false;
    /** The URL to the download file. */
    @Prop() downloadUrl?: string;
    /** The text for the download link. */
    @Prop() downloadLabel?: string;
    /** Additional information for the download link. */
    @Prop() downloadInfo?: string;
    /** The file name for the download link. */
    @Prop() downloadFileName: string = "technical-data.pdf";

    componentWillLoad() {
        // check if location has a hash that matches an ID from inside the content container
        const hash = window.location.hash;
        if (hash && document.querySelector(`${baseClass} ${hash}`)) {
            this.opened = true;
        }
    }

    render() {
        return (
            <Host class={baseClass}>
                <div class={`${baseClass}__header`}>
                    <div class={`${baseClass}__items`}>
                        <slot name="item" />
                    </div>
                    {!!this.openLabel && (
                        <button
                            class={`${baseClass}__toggle-button`}
                            type="button"
                            onClick={() => {
                                this.opened = !this.opened;
                                if (this.opened) {
                                    track("click_open_technical_data", {});
                                }
                            }}
                            aria-expanded={this.opened.toString()}
                            aria-controls={`${this.uid}__content`}
                        >
                            {this.opened ? this.closeLabel : this.openLabel}
                            <ux-icon name="plus"></ux-icon>
                        </button>
                    )}
                </div>
                <div
                    id={`${this.uid}__content`}
                    class={`${baseClass}__content-container`}
                    aria-hidden={!this.opened}
                    aria-expanded={this.opened}
                    role="region"
                    aria-labelledby={`${this.uid}__title`}
                    inert={!this.opened}
                >
                    <div class={`${baseClass}__content`}>
                        <div
                            id={`${this.uid}__title`}
                            class={`${baseClass}__title`}
                        >
                            <slot name="title" />
                        </div>
                        <slot name="content" />
                        {!!this.downloadUrl && !!this.downloadLabel && (
                            <ux-iws-nb-technical-data-download-button
                                download-url={this.downloadUrl}
                                download-label={this.downloadLabel}
                                download-info={this.downloadInfo}
                                download-file-name={this.downloadFileName}
                            />
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
